import React, { useState, useEffect, useCallback } from 'react';
import { X, User, Mail, Clock, CheckCircle2, FileText, ArrowRight, Sparkles } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { apiGet } from '../../utils/api';
import AskJPro from './AskJPro';

const STATUS_THEME = {
  completed:          { label: 'Final Report Sent', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  review_complete:    { label: 'Review Complete', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  all_received:       { label: 'All Received', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  in_review:          { label: 'In Review', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  partially_received: { label: 'Partial Upload', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  files_requested:    { label: 'Pending Upload', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  files_selected:     { label: 'Files Selected', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  setup_requested:    { label: 'Setup Requested', cls: 'bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300' },
  scheduled:          { label: 'Scheduled', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  paused:             { label: 'Paused', cls: 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300' },
  unresponsive:       { label: 'Pending', cls: 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300' },
};

const TF_STATUS = {
  final_approval: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  provisional_approval: { label: 'Provisional', cls: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300' },
  corrections_received: { label: 'Corr. Received', cls: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300' },
  corrections_requested: { label: 'Corr. Requested', cls: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300' },
  corrections_needed: { label: 'Corr. Needed', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  in_review: { label: 'In Review', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  file_received: { label: 'Received', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  pending: { label: 'Pending', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
};

const EVENT_LABELS = {
  status_change: 'Status Changed',
  file_received: 'Files Received',
  email_sent: 'Email Sent',
  file_selection_completed: 'Files Selected',
};

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ClientPropertySidePanel = ({ propertyId, onClose, onEmailClick }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDetail = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await apiGet(`/client/audit_tracker/properties/${propertyId}`);
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  }, [propertyId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  useEffect(() => { setActiveTab('overview'); }, [propertyId]);

  if (!propertyId) return null;

  const property = data?.property;
  const contacts = data?.contacts || [];
  const events = data?.events || [];
  const tenantFiles = data?.tenant_files || [];
  const visibleColumns = data?.visible_columns || [];
  const customLabels = data?.custom_labels || {};
  const primaryContact = contacts.find((c) => c.primary_contact) || contacts[0];

  const statusInfo = STATUS_THEME[property?.status] || STATUS_THEME.scheduled;

  const tabBtn = (key, label) => (
    <button
      onClick={() => setActiveTab(key)}
      className={`px-3 py-1.5 text-[12px] font-rasa font-medium rounded-lg transition-colors ${
        activeTab === key
          ? (d ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white')
          : (d ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')
      }`}
    >
      {label}
    </button>
  );

  const ProgressBar = ({ value, max, color = 'blue' }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
      <div className="flex items-center gap-2">
        <div className={`flex-1 h-1.5 rounded-full ${d ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div className={`h-1.5 rounded-full bg-${color}-500 transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`text-[11px] font-rasa font-medium ${d ? 'text-gray-400' : 'text-gray-500'}`}>{value}/{max} ({pct}%)</span>
      </div>
    );
  };

  return (
    <div className={`fixed top-0 right-0 h-full w-[440px] z-40 shadow-2xl border-l overflow-hidden flex flex-col ${d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className={`flex items-start justify-between px-5 py-4 border-b shrink-0 ${d ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex-1 min-w-0 mr-3">
          {loading ? (
            <div className={`h-5 w-48 rounded ${d ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse`} />
          ) : (
            <>
              <h3 className="text-base font-dm-serif truncate">{property?.property_name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${statusInfo.cls}`}>
                  {property?.status_label || statusInfo.label}
                </span>
                {property?.state && <span className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>{property.state}</span>}
              </div>
            </>
          )}
        </div>
        <button onClick={onClose} className={`p-1.5 rounded-lg shrink-0 ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
          <X size={16} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" /></div>
      ) : property && (
        <>
          {/* Quick action — email */}
          {primaryContact?.email && (
            <div className={`flex items-center gap-2 px-5 py-2.5 border-b shrink-0 ${d ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => onEmailClick?.(property, primaryContact)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700"
              >
                <Mail size={11} /> Email Property
              </button>
              <span className={`text-[11px] font-rasa truncate ${d ? 'text-gray-500' : 'text-gray-400'}`}>{primaryContact.email}</span>
            </div>
          )}

          {/* Tabs */}
          <div className={`flex items-center gap-1 px-5 py-2 border-b shrink-0 ${d ? 'border-gray-700' : 'border-gray-200'}`}>
            {tabBtn('overview', 'Overview')}
            {tabBtn('files', `Files (${tenantFiles.length})`)}
            {tabBtn('timeline', `Timeline (${events.length})`)}
            {tabBtn('jpro', 'Ask JPro')}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4">

            {/* ── Overview ── */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Progress */}
                <div className="space-y-2">
                  <div>
                    <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Files Collected</span>
                    <ProgressBar value={property.files_received || 0} max={property.files_required || 0} color="blue" />
                  </div>
                  <div>
                    <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Review Progress</span>
                    <ProgressBar value={property.files_completed || 0} max={property.files_required || 0} color="emerald" />
                  </div>
                </div>

                {/* Details grid */}
                <div className={`grid grid-cols-2 gap-3 p-3 rounded-lg ${d ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  {[
                    { l: 'Audit Month', v: property.audit_month ? MONTHS[property.audit_month] : '-' },
                    { l: 'State', v: property.state || '-' },
                    { l: 'Units', v: property.qty_units || '-' },
                    { l: 'Affordable Units', v: property.qty_affordable_units || '-' },
                    { l: 'Start Date', v: property.start_date || '-' },
                    { l: 'Due Date', v: property.due_date || '-' },
                    { l: 'Completed', v: property.completed_date || '-' },
                    { l: 'Last Contact', v: property.last_contact_at ? new Date(property.last_contact_at).toLocaleDateString() : '-' },
                  ].map((i) => (
                    <div key={i.l}>
                      <div className={`text-[10px] font-rasa font-bold uppercase ${d ? 'text-gray-500' : 'text-gray-400'}`}>{i.l}</div>
                      <div className={`text-[13px] font-rasa ${d ? 'text-gray-200' : 'text-gray-700'}`}>{i.v}</div>
                    </div>
                  ))}
                </div>

                {/* Contacts */}
                {contacts.length > 0 && (
                  <div>
                    <div className={`text-[11px] font-rasa font-bold mb-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Contacts</div>
                    {contacts.map((c) => (
                      <div key={c.id} className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${d ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                        <User size={13} className={d ? 'text-gray-500' : 'text-gray-400'} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-[12px] font-rasa truncate ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                            {c.name || c.email}
                            {c.primary_contact && <span className="text-blue-500 text-[10px] ml-1">(Primary)</span>}
                          </div>
                          {c.email && <div className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>{c.email}</div>}
                          {c.phone && <div className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>{c.phone}</div>}
                        </div>
                        {c.email && (
                          <button
                            onClick={() => onEmailClick?.(property, c)}
                            className={`p-1 rounded ${d ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                            title="Email"
                          >
                            <Mail size={12} className="text-blue-500" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {property.management_company && (
                  <div className={`text-[12px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                    Management: <span className={d ? 'text-gray-300' : 'text-gray-600'}>{property.management_company}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Files ── */}
            {activeTab === 'files' && (
              <div>
                {tenantFiles.length === 0 ? (
                  <div className={`text-center py-8 text-sm font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    No tenant files yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px] font-rasa">
                      <thead className="sticky top-0">
                        <tr className={`border-b ${d ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                          <th className={`px-2 py-1.5 text-left font-bold ${d ? 'text-gray-400' : 'text-gray-500'}`}>Tenant</th>
                          {visibleColumns.slice(0, 4).map((col) => (
                            <th key={col} className={`px-2 py-1.5 text-left font-bold ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                              {(customLabels[col] || col).replace(/_/g, ' ')}
                            </th>
                          ))}
                          <th className={`px-2 py-1.5 text-left font-bold ${d ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenantFiles.map((tf) => {
                          const tfStatus = TF_STATUS[tf.status] || TF_STATUS.pending;
                          return (
                            <tr key={tf.id} className={`border-b ${d ? 'border-gray-700/30' : 'border-gray-100'}`}>
                              <td className={`px-2 py-1.5 ${d ? 'text-gray-300' : 'text-gray-700'}`}>{tf.tenant_name}</td>
                              {visibleColumns.slice(0, 4).map((col) => (
                                <td key={col} className={`px-2 py-1.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{tf[col] ?? '-'}</td>
                              ))}
                              <td className="px-2 py-1.5">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${tfStatus.cls}`}>
                                  {tfStatus.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Timeline ── */}
            {activeTab === 'timeline' && (
              <div>
                {events.length === 0 ? (
                  <div className={`text-center py-8 text-sm font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    No activity yet
                  </div>
                ) : (
                  <div className="space-y-1">
                    {events.map((e) => (
                      <div key={e.id} className={`flex gap-3 p-2.5 rounded-lg ${d ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}`}>
                        <div className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                          e.event_type === 'status_change' ? (d ? 'bg-blue-900/40' : 'bg-blue-100') :
                          e.event_type === 'file_received' ? (d ? 'bg-emerald-900/40' : 'bg-emerald-100') :
                          e.event_type === 'email_sent' ? (d ? 'bg-violet-900/40' : 'bg-violet-100') :
                          (d ? 'bg-gray-700' : 'bg-gray-100')
                        }`}>
                          {e.event_type === 'status_change' ? <ArrowRight size={11} className="text-blue-500" /> :
                           e.event_type === 'file_received' ? <CheckCircle2 size={11} className="text-emerald-500" /> :
                           e.event_type === 'email_sent' ? <Mail size={11} className="text-violet-500" /> :
                           <FileText size={11} className={d ? 'text-gray-400' : 'text-gray-500'} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-[12px] font-rasa font-medium ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                            {EVENT_LABELS[e.event_type] || e.event_type.replace(/_/g, ' ')}
                          </div>
                          {e.details && (
                            <div className={`text-[11px] font-rasa mt-0.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                              {e.event_type === 'status_change' && e.details.from && e.details.to
                                ? `${e.details.from.replace(/_/g, ' ')} → ${e.details.to.replace(/_/g, ' ')}`
                                : e.event_type === 'email_sent' && e.details.type
                                ? `${e.details.type.replace(/_/g, ' ')}${e.details.tier ? ` (Tier ${e.details.tier})` : ''}`
                                : null}
                            </div>
                          )}
                          <div className={`text-[10px] font-rasa mt-0.5 ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                            {new Date(e.created_at).toLocaleDateString()} {new Date(e.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Ask JPro ── */}
            {activeTab === 'jpro' && property && (
              <AskJPro
                propertyData={{
                  ...property,
                  contacts,
                  tenant_files: tenantFiles,
                  events: events.slice(0, 10),
                }}
                mode="property"
                audience="client"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ClientPropertySidePanel;
